import React from 'react';
import '../../styles/components/ModalTransferDetails.scss';
import { GaugeChart } from '@carbon/charts-react';
import { GaugeTypes, Statuses } from '@carbon/charts';
import { Subject, debounceTime } from 'rxjs';
import { SubManager } from '../../classes/SubManager';
import { language } from '../../services/LanguageService';
import { InlineNotification, Slider } from '../Carbon';
import { AsperaError } from '../../classes/AsperaError';
import { AsperaWrapper } from '../AsperaWrapper';
import { AsperaAccordion, AsperaAccordionItem } from '../AsperaAccordion';
import { AsperaDetailsData } from '../AsperaDetailsData';
import { AsperaCodeSnippet } from '../AsperaCodeSnippet';
/**
 * React component
 *
 * The Transfer info modal
 */
export class ModalTransferDetails extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            loading: true,
            error: false,
        };
        this.subManager = new SubManager();
        /** Max speed in Kbps for calculating percent */
        this.defaultMaxSpeed = 10000;
        this.targetRateChange = new Subject();
        this.lastUsedBytes = 0;
        this.lastUserSelectedRate = 0;
        this.timesRateIsWrong = 0;
        this.chartOptions = {
            height: '160px',
            width: '320px',
            toolbar: {
                enabled: false,
            },
            tooltip: {
                enabled: false,
            },
            color: {
                scale: {
                    value: '#24a148',
                },
            },
            gauge: {
                valueFontSize: () => 38,
                showPercentageSymbol: false,
                numberFormatter: (data) => {
                    var _a, _b, _c;
                    if (!((_a = this.localTransfer) === null || _a === void 0 ? void 0 : _a.isActive)) {
                        switch ((_b = this.localTransfer) === null || _b === void 0 ? void 0 : _b.status) {
                            case 'canceled':
                            case 'cancelled':
                            case 'paused':
                            case 'pausing':
                            case 'canceling':
                                return language.get('transferActivity.stopped');
                            case 'completed':
                                return language.get('transferActivity.completed');
                            case 'failed':
                                return language.get('transferActivity.failed');
                        }
                    }
                    // Carbon Charts started rounding data. This causes the numbers not to match. Round here to ensure they are the same
                    if (Math.round(data) === Math.round(this.lastUsedBytes)) {
                        return language.getBytes(((((_c = this.localTransfer) === null || _c === void 0 ? void 0 : _c.calcRateKbps) || 0) * 1000), 1, undefined, undefined, true);
                    }
                    else {
                        return language.get('transferInfo.percentItem', [data]);
                    }
                },
                type: GaugeTypes.SEMI,
                status: Statuses.SUCCESS,
            },
        };
        this.updateTargetRate = (value) => {
            if (this.localTransfer && typeof this.localTransfer.updateTransfer === 'function') {
                const newValue = value * 1000;
                if (this.localTransfer.connectStyleTransfer.target_rate_kbps === newValue) {
                    return;
                }
                this.localTransfer.connectStyleTransfer.target_rate_kbps = newValue;
                this.lastUserSelectedRate = newValue;
                this.setState({});
                this.localTransfer.updateTransfer({ target_rate_kbps: this.localTransfer.connectStyleTransfer.target_rate_kbps });
            }
            else {
                new AsperaError('ModalTransferDetails: unable to update due to no transfer', {}, true);
            }
        };
    }
    get chartTransferOptions() {
        var _a, _b;
        // eslint-disable-next-line
        let color = '#0043ce';
        switch ((_b = (_a = this.localTransfer) === null || _a === void 0 ? void 0 : _a.connectStyleTransfer) === null || _b === void 0 ? void 0 : _b.status) {
            case 'failed':
                color = '#da1e28';
                break;
            case 'completed':
                color = '#24a148';
        }
        return {
            height: '176px',
            width: '352px',
            toolbar: {
                enabled: false,
            },
            tooltip: {
                enabled: false,
            },
            color: {
                scale: {
                    value: color,
                },
            },
            gauge: {
                showPercentageSymbol: false,
                numberFormatter: () => '',
                type: GaugeTypes.SEMI,
                status: Statuses.SUCCESS,
            },
        };
    }
    get maxSpeedKbps() {
        var _a, _b;
        if (this.localTransfer) {
            if (this.defaultMaxSpeed < (this.localTransfer.connectStyleTransfer.target_rate_kbps || 0) + 10000) {
                this.defaultMaxSpeed *= 10;
            }
            if (((_a = this.localTransfer.connectStyleTransfer) === null || _a === void 0 ? void 0 : _a.target_rate_cap_kbps) && this.localTransfer.connectStyleTransfer.target_rate_cap_kbps <= this.defaultMaxSpeed) {
                return (_b = this.localTransfer.connectStyleTransfer) === null || _b === void 0 ? void 0 : _b.target_rate_cap_kbps;
            }
        }
        return this.defaultMaxSpeed;
    }
    get maxSpeed() {
        return this.maxSpeedKbps * 1000;
    }
    get infoItems() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11, _12, _13, _14, _15, _16, _17, _18, _19, _20, _21, _22, _23, _24, _25, _26, _27, _28, _29, _30, _31, _32, _33, _34, _35, _36, _37, _38, _39, _40, _41, _42, _43, _44, _45, _46, _47, _48, _49, _50, _51, _52, _53, _54, _55, _56, _57, _58, _59, _60, _61, _62, _63;
        return [
            {
                key: language.get('transferInfo.title'),
                value: (_a = this.localTransfer) === null || _a === void 0 ? void 0 : _a.transferTitle,
                hidden: !((_b = this.localTransfer) === null || _b === void 0 ? void 0 : _b.transferTitle),
            },
            {
                key: language.get('transferInfo.app'),
                value: (_c = this.localTransfer) === null || _c === void 0 ? void 0 : _c.appName,
            },
            {
                key: language.get('transferInfo.status'),
                value: (_d = this.localTransfer) === null || _d === void 0 ? void 0 : _d.displayDetailsStatus,
            },
            {
                key: language.get('transferInfo.errorDescription'),
                value: (_f = (_e = this.localTransfer) === null || _e === void 0 ? void 0 : _e.connectStyleTransfer) === null || _f === void 0 ? void 0 : _f.error_desc,
                hidden: !((_h = (_g = this.localTransfer) === null || _g === void 0 ? void 0 : _g.connectStyleTransfer) === null || _h === void 0 ? void 0 : _h.error_desc),
            },
            {
                key: language.get('transferInfo.errorCode'),
                value: (_k = (_j = this.localTransfer) === null || _j === void 0 ? void 0 : _j.connectStyleTransfer) === null || _k === void 0 ? void 0 : _k.error_code,
                hidden: !((_m = (_l = this.localTransfer) === null || _l === void 0 ? void 0 : _l.connectStyleTransfer) === null || _m === void 0 ? void 0 : _m.error_code),
            },
            {
                key: language.get('transferInfo.percent'),
                value: language.get('transferInfo.percentItem', [(_o = this.localTransfer) === null || _o === void 0 ? void 0 : _o.safeRoundedPercent]),
                hidden: !((_p = this.localTransfer) === null || _p === void 0 ? void 0 : _p.safeRoundedPercent),
            },
            {
                key: language.get('transferInfo.dataExpected'),
                value: language.getBytes(((_q = this.localTransfer) === null || _q === void 0 ? void 0 : _q.bytesExpected) || 0, 1),
                hidden: !((_r = this.localTransfer) === null || _r === void 0 ? void 0 : _r.bytesExpected),
            },
            {
                key: language.get('transferInfo.dataWritten'),
                value: language.getBytes(((_s = this.localTransfer) === null || _s === void 0 ? void 0 : _s.bytesWritten) || 0, 1),
                hidden: !((_t = this.localTransfer) === null || _t === void 0 ? void 0 : _t.bytesWritten),
            },
            {
                key: language.get('transferInfo.rate'),
                value: language.getBytes(((((_u = this.localTransfer) === null || _u === void 0 ? void 0 : _u.calcRateKbps) || 0) * 1000), 1, undefined, undefined, true),
                hidden: !((_v = this.localTransfer) === null || _v === void 0 ? void 0 : _v.calcRateKbps),
            },
            {
                key: language.get('transferInfo.ended'),
                value: language.getDate(((_x = (_w = this.localTransfer) === null || _w === void 0 ? void 0 : _w.connectStyleTransfer) === null || _x === void 0 ? void 0 : _x.end_time) || 0, 'medium'),
                hidden: !((_z = (_y = this.localTransfer) === null || _y === void 0 ? void 0 : _y.connectStyleTransfer) === null || _z === void 0 ? void 0 : _z.end_time),
            },
            {
                key: language.get('transferInfo.currentFile'),
                value: (_1 = (_0 = this.localTransfer) === null || _0 === void 0 ? void 0 : _0.connectStyleTransfer) === null || _1 === void 0 ? void 0 : _1.current_file,
                hidden: !((_3 = (_2 = this.localTransfer) === null || _2 === void 0 ? void 0 : _2.connectStyleTransfer) === null || _3 === void 0 ? void 0 : _3.current_file),
            },
            {
                key: language.get('transferInfo.filesAttempted'),
                value: (_6 = (_5 = (_4 = this.localTransfer) === null || _4 === void 0 ? void 0 : _4.connectStyleTransfer) === null || _5 === void 0 ? void 0 : _5.file_counts) === null || _6 === void 0 ? void 0 : _6.attempted,
                hidden: !((_9 = (_8 = (_7 = this.localTransfer) === null || _7 === void 0 ? void 0 : _7.connectStyleTransfer) === null || _8 === void 0 ? void 0 : _8.file_counts) === null || _9 === void 0 ? void 0 : _9.attempted),
            },
            {
                key: language.get('transferInfo.filesCompleted'),
                value: (_12 = (_11 = (_10 = this.localTransfer) === null || _10 === void 0 ? void 0 : _10.connectStyleTransfer) === null || _11 === void 0 ? void 0 : _11.file_counts) === null || _12 === void 0 ? void 0 : _12.completed,
                hidden: !((_15 = (_14 = (_13 = this.localTransfer) === null || _13 === void 0 ? void 0 : _13.connectStyleTransfer) === null || _14 === void 0 ? void 0 : _14.file_counts) === null || _15 === void 0 ? void 0 : _15.completed),
            },
            {
                key: language.get('transferInfo.filesFailed'),
                value: (_18 = (_17 = (_16 = this.localTransfer) === null || _16 === void 0 ? void 0 : _16.connectStyleTransfer) === null || _17 === void 0 ? void 0 : _17.file_counts) === null || _18 === void 0 ? void 0 : _18.failed,
                hidden: !((_21 = (_20 = (_19 = this.localTransfer) === null || _19 === void 0 ? void 0 : _19.connectStyleTransfer) === null || _20 === void 0 ? void 0 : _20.file_counts) === null || _21 === void 0 ? void 0 : _21.failed),
            },
            {
                key: language.get('transferInfo.filesSkipped'),
                value: (_24 = (_23 = (_22 = this.localTransfer) === null || _22 === void 0 ? void 0 : _22.connectStyleTransfer) === null || _23 === void 0 ? void 0 : _23.file_counts) === null || _24 === void 0 ? void 0 : _24.skipped,
                hidden: !((_27 = (_26 = (_25 = this.localTransfer) === null || _25 === void 0 ? void 0 : _25.connectStyleTransfer) === null || _26 === void 0 ? void 0 : _26.file_counts) === null || _27 === void 0 ? void 0 : _27.skipped),
            },
            {
                key: language.get('transferInfo.transport'),
                value: ((_29 = (_28 = this.localTransfer) === null || _28 === void 0 ? void 0 : _28.connectStyleTransfer) === null || _29 === void 0 ? void 0 : _29.transport) === 'fasp' ? language.get('transferInfo.fasp') : language.get('transferInfo.http'),
                hidden: !((_31 = (_30 = this.localTransfer) === null || _30 === void 0 ? void 0 : _30.connectStyleTransfer) === null || _31 === void 0 ? void 0 : _31.transport),
            },
            {
                key: language.get('transferInfo.remoteHost'),
                value: (_33 = (_32 = this.localTransfer) === null || _32 === void 0 ? void 0 : _32.transferSpec) === null || _33 === void 0 ? void 0 : _33.remote_host,
                hidden: !((_35 = (_34 = this.localTransfer) === null || _34 === void 0 ? void 0 : _34.transferSpec) === null || _35 === void 0 ? void 0 : _35.remote_host),
            },
            {
                key: language.get('transferInfo.remoteAccessKey'),
                value: (_37 = (_36 = this.localTransfer) === null || _36 === void 0 ? void 0 : _36.transferSpec) === null || _37 === void 0 ? void 0 : _37.remote_access_key,
                hidden: !((_39 = (_38 = this.localTransfer) === null || _38 === void 0 ? void 0 : _38.transferSpec) === null || _39 === void 0 ? void 0 : _39.remote_access_key),
            },
            {
                key: language.get('transferInfo.remoteUser'),
                value: (_41 = (_40 = this.localTransfer) === null || _40 === void 0 ? void 0 : _40.transferSpec) === null || _41 === void 0 ? void 0 : _41.remote_user,
                hidden: !((_43 = (_42 = this.localTransfer) === null || _42 === void 0 ? void 0 : _42.transferSpec) === null || _43 === void 0 ? void 0 : _43.remote_user),
            },
            {
                key: language.get('transferInfo.cipher'),
                value: (_45 = (_44 = this.localTransfer) === null || _44 === void 0 ? void 0 : _44.transferSpec) === null || _45 === void 0 ? void 0 : _45.cipher,
                hidden: !((_47 = (_46 = this.localTransfer) === null || _46 === void 0 ? void 0 : _46.transferSpec) === null || _47 === void 0 ? void 0 : _47.cipher),
            },
            {
                key: language.get('transferInfo.sshPort'),
                value: (_49 = (_48 = this.localTransfer) === null || _48 === void 0 ? void 0 : _48.transferSpec) === null || _49 === void 0 ? void 0 : _49.ssh_port,
                hidden: !((_51 = (_50 = this.localTransfer) === null || _50 === void 0 ? void 0 : _50.transferSpec) === null || _51 === void 0 ? void 0 : _51.ssh_port),
            },
            {
                key: language.get('transferInfo.faspPort'),
                value: (_53 = (_52 = this.localTransfer) === null || _52 === void 0 ? void 0 : _52.transferSpec) === null || _53 === void 0 ? void 0 : _53.fasp_port,
                hidden: !((_55 = (_54 = this.localTransfer) === null || _54 === void 0 ? void 0 : _54.transferSpec) === null || _55 === void 0 ? void 0 : _55.fasp_port),
            },
            {
                key: language.get('transferInfo.multiSession'),
                value: (_57 = (_56 = this.localTransfer) === null || _56 === void 0 ? void 0 : _56.transferSpec) === null || _57 === void 0 ? void 0 : _57.multi_session,
                hidden: (((_59 = (_58 = this.localTransfer) === null || _58 === void 0 ? void 0 : _58.transferSpec) === null || _59 === void 0 ? void 0 : _59.multi_session) || 0) <= 1,
            },
            {
                key: language.get('transferInfo.multiSessionThreshold'),
                value: language.getBytes(((_61 = (_60 = this.localTransfer) === null || _60 === void 0 ? void 0 : _60.transferSpec) === null || _61 === void 0 ? void 0 : _61.multi_session_threshold) || 0),
                hidden: !((_63 = (_62 = this.localTransfer) === null || _62 === void 0 ? void 0 : _62.transferSpec) === null || _63 === void 0 ? void 0 : _63.multi_session_threshold),
            },
        ];
    }
    get advancedItems() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        let spec = '';
        let transferData = '';
        try {
            spec = JSON.stringify((_a = this.localTransfer) === null || _a === void 0 ? void 0 : _a.transferSpec, undefined, 2);
            transferData = JSON.stringify((_b = this.localTransfer) === null || _b === void 0 ? void 0 : _b.connectStyleTransfer, undefined, 2);
        }
        catch (error) {
            new AsperaError('ModalTransferInfo: unable to parse transfer spec', error, true);
        }
        return [
            {
                key: language.get('transferInfo.uuid'),
                hidden: !((_d = (_c = this.localTransfer) === null || _c === void 0 ? void 0 : _c.connectStyleTransfer) === null || _d === void 0 ? void 0 : _d.uuid),
                value: React.createElement(AsperaCodeSnippet, { monoFont: true, value: ((_f = (_e = this.localTransfer) === null || _e === void 0 ? void 0 : _e.connectStyleTransfer) === null || _f === void 0 ? void 0 : _f.uuid) || '', type: "single" }),
            },
            {
                key: language.get('transferInfo.files'),
                value: (((_h = (_g = this.localTransfer) === null || _g === void 0 ? void 0 : _g.connectStyleTransfer) === null || _h === void 0 ? void 0 : _h.files) || []).map(data => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const file = data;
                    const fileName = file.name || file.file;
                    const fileSize = file.size || file.bytes_expected;
                    return fileSize ? `${fileName} (${language.getBytes(fileSize)})` : fileName;
                }).join(', '),
                hidden: !((_l = (_k = (_j = this.localTransfer) === null || _j === void 0 ? void 0 : _j.connectStyleTransfer) === null || _k === void 0 ? void 0 : _k.files) === null || _l === void 0 ? void 0 : _l.length),
            },
            {
                key: language.get('transferInfo.transferSpec'),
                value: React.createElement(AsperaCodeSnippet, { className: "code-snippet", monoFont: true, value: spec, type: "multi" }),
                hidden: !spec,
            },
            {
                key: language.get('transferInfo.fullTransfer'),
                value: React.createElement(AsperaCodeSnippet, { className: "code-snippet", monoFont: true, value: transferData, type: "multi" }),
                hidden: !transferData,
            },
        ];
    }
    get transferInfo() {
        return (React.createElement("div", { className: "details-transfer-info" },
            React.createElement(AsperaDetailsData, { items: this.infoItems, boldHeaders: true }),
            React.createElement(AsperaDetailsData, { items: this.advancedItems, boldHeaders: true, stacked: true })));
    }
    get transferSpeed() {
        var _a;
        const { transfer } = this.props;
        if (!this.localTransfer) {
            this.localTransfer = transfer;
        }
        const speedBits = (this.localTransfer.calcRateKbps || 0) * 1000;
        this.lastUsedBytes = speedBits ? (speedBits / this.maxSpeed) * 100 : 0;
        const data = [
            {
                group: 'value',
                value: this.lastUsedBytes,
            },
            {
                group: 'delta',
                value: this.localTransfer.safeRoundedPercent,
            },
        ];
        const transferData = [
            {
                group: 'value',
                value: this.localTransfer.safeRoundedPercent,
            },
        ];
        if (this.lastUserSelectedRate && this.localTransfer.connectStyleTransfer.target_rate_kbps && this.lastUserSelectedRate !== this.localTransfer.connectStyleTransfer.target_rate_kbps) {
            this.timesRateIsWrong++;
        }
        else {
            this.timesRateIsWrong = 0;
        }
        return (React.createElement("div", { className: `details-control-speed ${!((_a = this.localTransfer) === null || _a === void 0 ? void 0 : _a.isActive) ? 'non-active-transfer' : ''}` },
            React.createElement("div", { className: "transfer-gauge" },
                React.createElement("div", { className: "chart-item speed" },
                    React.createElement(GaugeChart, { data: data, options: this.chartOptions })),
                React.createElement("div", { className: "chart-item transfer" },
                    React.createElement(GaugeChart, { data: transferData, options: this.chartTransferOptions }))),
            this.localTransfer.canUpdateTransfer && React.createElement(React.Fragment, null,
                React.createElement(Slider, { ariaLabelInput: language.get('transferInfo.speedTargetRate'), id: "target-rate", className: "slider-control", labelText: language.get('transferInfo.speedTargetRate'), max: Math.floor(this.maxSpeedKbps / 1000), min: 0, step: 1, onChange: (value) => {
                        this.targetRateChange.next(value.value);
                    }, disabled: this.localTransfer.connectStyleTransfer.lock_target_rate, value: (this.localTransfer.connectStyleTransfer.target_rate_kbps || 0) / 1000 }),
                this.timesRateIsWrong >= 3 && React.createElement(InlineNotification, { lowContrast: true, hideCloseButton: true, kind: "info", title: language.get('transferInfo.rateUnableChange', [language.getBytes((this.localTransfer.connectStyleTransfer.target_rate_kbps || 0) * 1000, 1, undefined, undefined, true)]) }))));
    }
    componentDidUpdate(prevProps) {
        const { transfer } = this.props;
        if (transfer.id !== prevProps.transfer.id) {
            this.localTransfer = transfer;
            this.setState({});
        }
    }
    componentDidMount() {
        const { transferUpdateObservable } = this.props;
        this.subManager.setSubscription(transferUpdateObservable.subscribe({
            next: transfers => {
                const newTransfer = transfers.find(transfer => { var _a; return transfer.id === ((_a = this.localTransfer) === null || _a === void 0 ? void 0 : _a.id); });
                if (newTransfer) {
                    this.localTransfer = newTransfer;
                    this.setState({});
                }
            },
            error: error => {
                new AsperaError('ModalTransferDetails: unable to monitor transfers', error, true);
            },
        }));
        this.subManager.setSubscription(this.targetRateChange.pipe(debounceTime(500)).subscribe({
            next: value => {
                this.updateTargetRate(value);
            },
            error: error => {
                new AsperaError('ModalTransferDetails: Unable to monitor target rate controller', error, true);
            },
        }));
        this.setState({ loading: false });
    }
    componentWillUnmount() {
        const { modalClose } = this.props;
        this.subManager.unsubscribeAll();
        modalClose();
    }
    render() {
        const { loading, error } = this.state;
        const { initialOpen } = this.props;
        return (React.createElement(AsperaWrapper, { className: "modal-transfer-details", disableWrapping: true, loading: loading, error: error },
            React.createElement(AsperaAccordion, null,
                React.createElement(AsperaAccordionItem, { open: initialOpen === 'SPEED' || !initialOpen, title: language.get('transferInfo.transferSpeed') }, this.transferSpeed),
                React.createElement(AsperaAccordionItem, { open: initialOpen === 'INFO' || !initialOpen, title: language.get('transferInfo.transferInfo') }, this.transferInfo))));
    }
}
